// The plates of a recording (PRD-0074 slice 07, ADR-0087): the readings beside the faces, and the
// officer's decision on one — a confirm links it to the vehicle with that plate (made when absent),
// a reject keeps it as evidence linked to nothing, a reopen makes it a suggestion again. Every
// fetch lives here; the list under `components/` only draws (ADR-0039).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { canDecideFaces } from '@/features/face-matching/decision-role'
import { ApiError } from '@/services/api/client'
import {
  type PlateDecision,
  type PlateReading,
  type VideoPlatesResponse,
  videoApi,
} from '@/services/api/video'
import { videoKeys } from './useDocumentVideo'

export const plateKeys = {
  list: (caseId: string, documentId: string) =>
    [...videoKeys.all(caseId), documentId, 'plates'] as const,
}

/** Why the last decision was refused, as an i18n suffix under `documents.detail.plates.error`. */
export type PlateActionError = 'forbidden' | 'gone' | 'failed'

/** What an officer may do with a reading: link it (with the number as they read it), reject, reopen. */
export interface PlateActions {
  decide: (plateId: string, decision: PlateDecision, text?: string | null) => Promise<boolean>
  /** The reading a decision is in flight for; its controls wait. */
  pendingPlateId: string | null
  error: PlateActionError | null
}

export interface UseDocumentPlatesResult {
  plates: PlateReading[]
  isLoading: boolean
  /** Absent for a viewer (PRD-0032 SC-036): the list shows every reading and offers nothing. */
  actions?: PlateActions
}

const refusal = (error: unknown): PlateActionError => {
  if (error instanceof ApiError && error.status === 403) return 'forbidden'
  if (error instanceof ApiError && error.status === 404) return 'gone'
  return 'failed'
}

/** The plate readings of one open recording; nothing is fetched for a document that is no video. */
export function useDocumentPlates(
  caseId: string,
  documentId: string | null,
  enabled: boolean
): UseDocumentPlatesResult {
  const queryClient = useQueryClient()
  const readId = enabled ? documentId : null
  const query = useQuery<VideoPlatesResponse>({
    queryKey: plateKeys.list(caseId, readId ?? ''),
    queryFn: () => videoApi.plates(caseId, readId as string),
    enabled: Boolean(caseId && readId),
    staleTime: 60_000,
  })
  const [pendingPlateId, setPendingPlateId] = useState<string | null>(null)
  const [error, setError] = useState<PlateActionError | null>(null)
  const decision = useMutation({
    mutationFn: ({
      plateId,
      verdict,
      text,
    }: {
      plateId: string
      verdict: PlateDecision
      text?: string | null
    }) => videoApi.decidePlate(caseId, documentId as string, plateId, verdict, text),
    onSuccess: plate => {
      // The answer is the reading as it now stands: patched into the list, and the tracks
      // re-read so the box on the player takes the new tone.
      queryClient.setQueryData<VideoPlatesResponse>(
        plateKeys.list(caseId, documentId ?? ''),
        current =>
          current && {
            ...current,
            plates: current.plates.map(p => (p.plate_id === plate.plate_id ? plate : p)),
          }
      )
      void queryClient.invalidateQueries({
        queryKey: videoKeys.tracks(caseId, documentId ?? ''),
      })
    },
  })
  const { mutateAsync } = decision
  const decide = useCallback(
    async (plateId: string, verdict: PlateDecision, text?: string | null) => {
      if (!documentId) return false
      setError(null)
      setPendingPlateId(plateId)
      try {
        await mutateAsync({ plateId, verdict, text })
        return true
      } catch (caught) {
        setError(refusal(caught))
        return false
      } finally {
        setPendingPlateId(null)
      }
    },
    [documentId, mutateAsync]
  )
  return {
    plates: query.data?.plates ?? [],
    isLoading: query.isLoading,
    actions: canDecideFaces(query.data?.member_role)
      ? { decide, pendingPlateId, error }
      : undefined,
  }
}
